'use client';

import { useState } from 'react';
import {
  PayPalScriptProvider,
  PayPalButtons as PayPalButtonsSdk,
} from '@paypal/react-paypal-js';
import { Spinner } from '@/src/components/ui';

type Props = {
  projectId: number;
  onSuccess: () => void;
  onCancel: () => void;
  onError: (e: unknown) => void;
};

export function PayPalPurchaseButtons({
  projectId,
  onSuccess,
  onCancel,
  onError,
}: Props) {
  const [isCapturing, setIsCapturing] = useState(false);

  const clientId = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID!;
  const options = {
    clientId,
    currency: 'USD',
    intent: 'capture',
    components: 'buttons',
  } as const;

  return (
    <div className="relative">
      <PayPalScriptProvider options={options}>
        <PayPalButtonsSdk
          style={{ layout: 'vertical' }}
          disabled={isCapturing}
          createOrder={async () => {
            const res = await fetch('/api/paypal/orders/purchase', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ projectId }),
            });

            const json = await res.json();
            if (!res.ok || !json?.ok || !json?.orderId) {
              throw new Error(json?.error || 'Failed to create PayPal order');
            }

            return json.orderId as string;
          }}
          onApprove={async (data, actions) => {
            const orderId = data.orderID;
            setIsCapturing(true);

            try {
              const res = await fetch(`/api/paypal/orders/${orderId}/capture`, {
                method: 'POST',
              });

              const json = await res.json();

              const issue =
                json?.error?.details?.[0]?.issue ??
                json?.error?.name ??
                json?.error?.error?.details?.[0]?.issue;

              if (
                issue === 'INSTRUMENT_DECLINED' ||
                issue === 'TRANSACTION_REFUSED'
              ) {
                setIsCapturing(false);
                return actions?.restart();
              }

              if (!res.ok || !json?.ok) {
                throw new Error(
                  json?.error || 'Failed to capture PayPal order',
                );
              }

              onSuccess();
            } catch (e) {
              onError(e);
            } finally {
              setIsCapturing(false);
            }
          }}
          onCancel={() => {
            setIsCapturing(false);
            onCancel();
          }}
          onError={(e) => {
            setIsCapturing(false);
            onError(e);
          }}
        />
      </PayPalScriptProvider>
      {isCapturing && (
        <div className="mt-2 flex items-center justify-center gap-2 text-sm text-black/60">
          <Spinner /> Finalizing payment…
        </div>
      )}
    </div>
  );
}
